import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Clock, Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLang } from '@/i18n/LanguageContext';

const programs = [
  {
    id: 'dental-assisting',
    image: '/images/programs/dental_assistant_new.jpg',
    titleKey: 'dentalAssisting',
    price: '$3,650',
    priceNote: 'Gastonia / Lake Norman',
    duration: '12 weeks',
    badge: 'Most Popular',
    badgeColor: 'bg-amber-400 text-amber-900',
    link: 'https://form.jotform.com/232094520728051',
  },
  {
    id: 'orthodontic',
    image: '/images/programs/orthodontic_new.jpg',
    titleKey: 'orthodonticAssistant',
    price: '$4,550',
    priceNote: 'Lake Norman Campus',
    duration: '12 weeks',
    badge: 'Specialty',
    badgeColor: 'bg-brand-secondary text-brand-primary',
    link: 'https://form.jotform.com/232094520728051',
  },
  {
    id: 'radiology',
    image: '/images/programs/radiology_new.jpg',
    titleKey: 'ncRadiology',
    price: '$650',
    priceNote: 'All campuses',
    duration: '',
    badge: 'CE Course',
    badgeColor: 'bg-brand-secondary text-brand-primary',
    link: 'https://form.jotform.com/242294621286055',
  },
  {
    id: 'continued-education',
    image: '/images/programs/polishing_new.jpg',
    titleKey: 'continuedEducation',
    price: 'Multiple',
    priceNote: 'CE Courses',
    duration: '',
    badge: 'CE Course',
    badgeColor: 'bg-brand-secondary text-brand-primary',
    link: 'https://form.jotform.com/242294621286055',
    features: [
      { name: 'CPR Certification', price: '$50' },
      { name: 'SPICE', price: '$65' },
      { name: 'Radiology Challenge Exam', price: '$350' },
      { name: 'Coronal Polishing', price: '$375' }
    ]
  },
];

const programTitles = {
  en: {
    dentalAssisting: 'Dental Assistant I',
    orthodonticAssistant: 'Orthodontic Assistant',
    ncRadiology: 'NC Radiology',
    continuedEducation: 'Continued Education',
  },
  es: {
    dentalAssisting: 'Asistente Dental I',
    orthodonticAssistant: 'Asistente de Ortodoncia',
    ncRadiology: 'Radiología NC',
    continuedEducation: 'Educación Continua',
  }
};

const ProgramCards = () => {
  const { lang } = useLang();
  const navigate = useNavigate();
  const titles = programTitles[lang] || programTitles.en;

  const handleNav = (link) => {
    if (link && link.startsWith('http')) {
      window.open(link, '_blank');
    } else {
      navigate('/application');
      window.scrollTo(0, 0);
    }
  };

  return (
    <section className="relative bg-slate-950 py-14 sm:py-20 md:py-28 overflow-hidden">
      <div className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '28px 28px'
        }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-400/10 border border-amber-400/20 text-amber-300 text-[11px] font-bold uppercase tracking-[0.18em] mb-5">
            <Star className="w-3 h-3 fill-amber-300" />
            {lang === 'es' ? 'Programas & Precios' : 'Programs & Pricing'}
          </div>
          <h2 className="text-3xl md:text-5xl font-extrabold text-white tracking-tight">
            {lang === 'es' ? 'Elige tu camino' : 'Choose Your Path'}
          </h2>
          <p className="mt-4 text-lg text-slate-400 max-w-xl mx-auto">
            {lang === 'es'
              ? 'Entrenamiento práctico, apoyo laboral, horarios flexibles, planes de pago internos, gradúate sin deuda • Programas Bilingües.'
              : 'Hands-On Training • Job Placement Support • Flexible Schedules • In-House Payment Plans • Graduate Debt-Free • Bilingual Programs'}
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {programs.map((prog, i) => (
            <motion.div
              key={prog.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              whileHover={{ y: -6 }}
              onClick={() => handleNav(prog.link)}
              className="group flex flex-col relative bg-slate-900 rounded-2xl overflow-hidden border border-slate-800 hover:border-slate-600 transition-all duration-300 cursor-pointer shadow-xl hover:shadow-2xl hover:shadow-black/40 h-full"
            >
              <div className="relative h-48 shrink-0 overflow-hidden">
                <img
                  src={prog.image}
                  alt={titles[prog.titleKey]}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-in-out"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-primary/60 to-transparent opacity-60 group-hover:opacity-40 transition-opacity duration-300" />
                <div className="absolute top-3 left-3">
                  <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${prog.badgeColor}`}>
                    {prog.badge}
                  </span>
                </div>
              </div>
              <div className="p-5 flex-1 flex flex-col">
                <h3 className="text-base font-bold text-white leading-tight mb-3 group-hover:text-amber-300 transition-colors">
                  {titles[prog.titleKey]}
                </h3>

                {prog.features ? (
                  <ul className="mb-4 space-y-1.5 min-h-[52px]">
                    {prog.features.map((feat, idx) => {
                      const isObj = typeof feat === 'object';
                      return (
                        <li key={idx} className="flex items-center justify-between text-[12px] font-medium text-slate-300 w-full">
                          <div className="flex items-center">
                            <span className="w-1.5 h-1.5 rounded-full bg-brand-secondary mr-2 shrink-0" />
                            {isObj ? feat.name : feat}
                          </div>
                          {isObj && feat.price && (
                            <span className="text-white font-bold">{feat.price}</span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <div className="flex items-end justify-between mb-4 min-h-[52px]">
                    <div>
                      <div className="text-2xl font-extrabold text-white">{prog.price}</div>
                      <div className="text-[11px] text-slate-500 mt-0.5">{prog.priceNote}</div>
                    </div>
                    {prog.duration && (
                      <div className="flex items-center gap-1 text-slate-400 text-[11px] font-semibold mb-1">
                        <Clock className="w-3.5 h-3.5" />
                        {prog.duration}
                      </div>
                    )}
                  </div>
                )}

                <div className="mt-auto pt-4">
                  <div className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-brand-primary hover:opacity-80 text-white text-sm font-semibold transition-opacity">
                    {lang === 'es' ? 'Aplicar Ahora' : 'Apply Now'}
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <p className="text-center text-slate-500 text-sm mt-8">
          {lang === 'es'
            ? '* Los precios pueden variar. Contáctanos para más información.'
            : '* Prices may vary. Contact us for more details.'}
        </p>
      </div>
    </section>
  );
};

export default ProgramCards;
